import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Copy, GripVertical, ListPlus, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { CardContent, CardHeader, CardTitle } from '../ui/card';
import { cn } from '../../lib/utils';
import { hasSetups, shotNumbers } from '../../lib/treatmentShots';
import { formatRunningTime } from '../../lib/treatmentHelpers';
import { thumbnailUrl } from '../../lib/treatmentReferences';
import ShotNumber from './ShotNumber';

function OutlineRow({ shot, number, selected, onSelect }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: shot.id });
  const cover = shot.references?.[0]?.image_url;
  const firstFrame = cover ? thumbnailUrl(cover) : null;
  const sub = [
    shot.duration_seconds ? formatRunningTime(shot.duration_seconds) : null,
    shot.framing || null,
  ].filter(Boolean).join(' · ');

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center rounded-cards',
        shot.parent_id && 'ml-4',
        selected ? 'bg-mist' : 'hover:bg-mist/60',
        isDragging && 'opacity-60',
      )}
    >
      <button
        type="button"
        className="h-9 w-5 shrink-0 flex items-center justify-center text-ink-faded cursor-grab"
        aria-label={`Reorder shot ${number}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={() => onSelect(shot.id)}
        aria-current={selected ? 'true' : undefined}
        className="flex flex-1 min-w-0 items-center gap-1.5 py-1 pr-2 text-left"
        data-testid={`outline-shot-${number}`}
      >
        <ShotNumber number={number} className="min-w-[2.25rem]" />
        {firstFrame ? (
          <img src={firstFrame} alt="" referrerPolicy="no-referrer" loading="lazy" className="w-14 h-6 rounded-[4px] object-cover shrink-0 bg-mist" />
        ) : (
          <span className="w-14 h-6 rounded-[4px] bg-mist shrink-0" aria-hidden="true" />
        )}
        <span className="flex flex-col min-w-0">
          <span className={cn('truncate text-sm leading-tight', selected ? 'font-medium text-foreground' : 'font-normal')}>
            {shot.title || <span className="text-muted-foreground italic">Untitled</span>}
          </span>
          {sub && <span className="text-xs leading-tight text-muted-foreground truncate">{sub}</span>}
        </span>
      </button>
    </div>
  );
}

/**
 * The right of the shots panel: every shot in order, setups indented under their sequence.
 * Drag rules (a sequence moves with its setups, a setup stays in its
 * sequence) are lib/treatmentShots.moveShot; this only reports the drop.
 */
export default function TreatmentShotOutline({ shots, selectedId, onSelect, onMove, onAddShot, onAddSetup, onDuplicate, onRemove }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const numbers = shotNumbers(shots);
  const selected = shots.find((s) => s.id === selectedId);
  const number = selected && numbers[selected.id];
  const isSequence = selected && hasSetups(shots, selected.id);

  const handleDragEnd = ({ active, over }) => {
    if (over && active.id !== over.id) onMove(active.id, over.id);
  };

  return (
    <section aria-label="Shot list" className="lg:flex lg:flex-col lg:min-h-0">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 h-14 px-4 py-0 border-b border-line shrink-0">
        <CardTitle className="text-lg">
          Shots
        </CardTitle>
        {/* Add shot (after the selected one), then the selected shot's own actions */}
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={selected ? `Add a shot after ${number}` : 'Add shot'}
            title="Add shot"
            className="h-8 w-8 text-muted-foreground"
            onClick={() => onAddShot()}
            data-testid="add-shot-btn"
          >
            <Plus className="w-4 h-4" />
          </Button>
          {selected && (
            <>
              {/* From a setup, this adds another setup to the same sequence. */}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={selected.parent_id || isSequence ? `Add a setup to sequence ${numbers[selected.parent_id || selected.id]}` : `Make shot ${number} a sequence`}
                title={selected.parent_id || isSequence ? 'Add setup' : 'Make sequence'}
                className="h-8 w-8 text-muted-foreground"
                onClick={() => onAddSetup(selected.parent_id || selected.id)}
                data-testid="add-setup-btn"
              >
                <ListPlus className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={isSequence ? `Duplicate sequence ${number} with its setups` : `Duplicate shot ${number}`}
                title="Duplicate"
                className="h-8 w-8 text-muted-foreground"
                onClick={() => onDuplicate(selected.id)}
                data-testid="duplicate-shot-btn"
              >
                <Copy className="w-4 h-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Delete shot ${number}`}
                title="Delete"
                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                onClick={() => onRemove(selected.id)}
                data-testid="delete-shot-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-2 lg:min-h-0 lg:overflow-y-auto scrollbar-thin">
        <nav aria-label="Shots" className="flex flex-col gap-0.5">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={shots.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              {shots.map((shot) => (
                <OutlineRow
                  key={shot.id}
                  shot={shot}
                  number={numbers[shot.id]}
                  selected={shot.id === selectedId}
                  onSelect={onSelect}
                />
              ))}
            </SortableContext>
          </DndContext>
          {shots.length === 0 && <p className="text-sm text-muted-foreground px-2 py-2">No shots yet.</p>}
        </nav>
      </CardContent>
    </section>
  );
}
